/** Thin fetch helpers for the public registration HTTP contract (SPEC §9, routes/register.ts). */
import type {
  PublicFormConfig,
  RegisterInput,
  RegisterResponse,
  RegistrationDetails,
  SlotDto,
} from '@ffs/shared';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public fields: Record<string, string[]> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface SlotsResponse {
  slots: SlotDto[];
  server_now: number;
}

export interface RescheduleInfo {
  current_start_at: number;
  current_label: string;
  webinar_title: string;
  slots: SlotDto[];
}

async function request<T>(
  url: string,
  init: { method?: string; json?: unknown; headers?: Record<string, string> } = {},
): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json', ...(init.headers ?? {}) };
  let body: BodyInit | undefined;
  if (init.json !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(init.json);
  }
  let res: Response;
  try {
    res = await fetch(url, {
      method: init.method ?? 'GET',
      headers,
      body,
      cache: 'no-store',
      credentials: 'omit',
    });
  } catch {
    throw new ApiError(0, 'network', 'We could not reach the server.');
  }
  if (!res.ok) {
    let code = 'http_error';
    let message = res.status === 429 ? 'Too many attempts' : res.statusText || 'Request failed';
    let fields: Record<string, string[]> = {};
    try {
      const data = (await res.json()) as {
        error?: { code?: string; message?: string; fields?: Record<string, string[]> };
      };
      code = data.error?.code ?? code;
      message = data.error?.message ?? message;
      fields = data.error?.fields ?? {};
    } catch {
      // Non-JSON error body (e.g. a proxy page); keep the HTTP-level defaults.
    }
    throw new ApiError(res.status, code, message, fields);
  }
  return (await res.json()) as T;
}

const enc = encodeURIComponent;

export function getForm(base: string, publicId: string): Promise<PublicFormConfig> {
  return request(`${base}/api/forms/${enc(publicId)}`);
}

export function getSlots(base: string, publicId: string, tz: string): Promise<SlotsResponse> {
  return request(`${base}/api/forms/${enc(publicId)}/slots?tz=${enc(tz)}`);
}

export function postRegister(
  base: string,
  input: RegisterInput,
  hosted: boolean,
): Promise<RegisterResponse> {
  return request(`${base}/api/register`, {
    method: 'POST',
    json: input,
    headers: hosted ? { 'X-FFS-Source': 'hosted' } : {},
  });
}

const registrationCache = new Map<string, Promise<RegistrationDetails>>();

/**
 * Confirmation details for an existing registration (hosted confirmation page). Shared between
 * the page's merge tags and its blocks, so one page makes one request however many blocks it has.
 */
export function getRegistration(base: string, token: string): Promise<RegistrationDetails> {
  const key = `${base} ${token}`;
  let p = registrationCache.get(key);
  if (!p) {
    p = request<RegistrationDetails>(`${base}/api/rooms/${enc(token)}/registration`);
    p.catch(() => registrationCache.delete(key));
    registrationCache.set(key, p);
  }
  return p;
}

export function getReschedule(base: string, token: string, tz: string): Promise<RescheduleInfo> {
  return request(`${base}/api/rooms/${enc(token)}/reschedule?tz=${enc(tz)}`);
}

export function postReschedule(
  base: string,
  token: string,
  slot_start_at: number | 'next_jit',
  timezone: string,
): Promise<RegisterResponse> {
  return request(`${base}/api/rooms/${enc(token)}/reschedule`, {
    method: 'POST',
    json: { slot_start_at, timezone },
  });
}
